import React, { FC, useMemo, useState } from "react";
import { Tabs, Button, Popover } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import styles from "./matter-timeline.css";

dayjs.extend(quarterOfYear);

const {
  ["period-selector"]: periodSelectorClassName,
  ["period-selector-content"]: periodSelectorContentClassName,
  ["period-selector-tabs"]: periodSelectorTabsClassName,
  ["period-selector-grid"]: periodSelectorGridClassName,
  ["period-selector-button"]: periodSelectorButtonClassName,
  ["period-selector-button-selected"]: periodSelectorButtonSelectedClassName,
} = styles as any;

type Props = {
  matterStartDate: Date | null;
  matterDeadlineDate: Date | null;
  value?: { type: "year" | "quarter" | "month"; value: string } | null;
  onChange?: (value: { type: "year" | "quarter" | "month"; value: string } | null) => void;
};

const { TabPane } = Tabs;

export const PeriodSelector: FC<Props> = ({
  matterStartDate,
  matterDeadlineDate,
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>("year");

  const { years, quarters, months } = useMemo(() => {
    if (!matterStartDate || !matterDeadlineDate) {
      return { years: [], quarters: [], months: [] };
    }

    const start = dayjs(matterStartDate);
    const end = dayjs(matterDeadlineDate);
    const years: string[] = [];
    const quarters: { year: string; quarter: number }[] = [];
    const months: { year: string; month: number }[] = [];

    // Helper function to check if a date range overlaps with the matter's range
    const overlapsWithMatter = (rangeStart: dayjs.Dayjs, rangeEnd: dayjs.Dayjs) => {
      return (
        (rangeStart.isBefore(end) || rangeStart.isSame(end)) &&
        (rangeEnd.isAfter(start) || rangeEnd.isSame(start))
      );
    };

    let current = start.startOf("year");
    const endYear = end.endOf("year");

    while (current.isBefore(endYear) || current.isSame(endYear, "year")) {
      const yearStart = current.startOf("year");
      const yearEnd = current.endOf("year");

      if (overlapsWithMatter(yearStart, yearEnd)) {
        const year = current.format("YYYY");
        years.push(year);

        // Add quarters for this year
        for (let q = 1; q <= 4; q++) {
          const quarterStart = current.quarter(q).startOf("quarter");
          const quarterEnd = quarterStart.endOf("quarter");
          if (overlapsWithMatter(quarterStart, quarterEnd)) {
            quarters.push({ year, quarter: q });
          }
        }

        // Add months for this year
        for (let m = 0; m < 12; m++) {
          const monthStart = current.month(m).startOf("month");
          const monthEnd = monthStart.endOf("month");
          if (overlapsWithMatter(monthStart, monthEnd)) {
            months.push({ year, month: m });
          }
        }
      }

      current = current.add(1, "year");
    }

    return { years, quarters, months };
  }, [matterStartDate, matterDeadlineDate]);

  const handleSelect = (type: "year" | "quarter" | "month", selectedValue: string) => {
    onChange?.({ type, value: selectedValue });
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    if (!value) return "Select period";

    switch (value.type) {
      case "year":
        return `Year ${value.value}`;
      case "quarter": {
        const [year, quarter] = value.value.split("-Q");
        return `Q${quarter} ${year}`;
      }
      case "month": {
        const [year, month] = value.value.split("-");
        return dayjs(`${year}-${month}-01`).format("MMMM YYYY");
      }
      default:
        return "Select period";
    }
  };

  const renderYearGrid = () => {
    const yearsByRow: string[][] = [];
    for (let i = 0; i < years.length; i += 4) {
      yearsByRow.push(years.slice(i, i + 4));
    }

    return (
      <div className={periodSelectorGridClassName}>
        {yearsByRow.map((row, rowIndex) => (
          <div key={rowIndex} style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            {row.map((year) => (
              <Button
                key={year}
                className={`${periodSelectorButtonClassName} ${
                  value?.type === "year" && value.value === year
                    ? periodSelectorButtonSelectedClassName
                    : ""
                }`}
                onClick={() => handleSelect("year", year)}
                type={value?.type === "year" && value.value === year ? "primary" : "default"}
              >
                {year}
              </Button>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderQuarterGrid = () => {
    const quartersByYear: { [key: string]: number[] } = {};
    quarters.forEach(({ year, quarter }) => {
      if (!quartersByYear[year]) {
        quartersByYear[year] = [];
      }
      quartersByYear[year].push(quarter);
    });

    return (
      <div className={periodSelectorGridClassName}>
        {Object.entries(quartersByYear).map(([year, yearQuarters]) => (
          <div key={year} style={{ marginBottom: "16px" }}>
            <div style={{ marginBottom: "8px", fontWeight: "bold" }}>{year}</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {yearQuarters.map((quarter) => (
                <Button
                  key={`${year}-Q${quarter}`}
                  className={`${periodSelectorButtonClassName} ${
                    value?.type === "quarter" && value.value === `${year}-Q${quarter}`
                      ? periodSelectorButtonSelectedClassName
                      : ""
                  }`}
                  onClick={() => handleSelect("quarter", `${year}-Q${quarter}`)}
                  type={
                    value?.type === "quarter" && value.value === `${year}-Q${quarter}`
                      ? "primary"
                      : "default"
                  }
                >
                  Q{quarter}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMonthGrid = () => {
    const monthsByYear: { [key: string]: number[] } = {};
    months.forEach(({ year, month }) => {
      if (!monthsByYear[year]) {
        monthsByYear[year] = [];
      }
      monthsByYear[year].push(month);
    });

    return (
      <div className={periodSelectorGridClassName}>
        {Object.entries(monthsByYear).map(([year, yearMonths]) => (
          <div key={year} style={{ marginBottom: "16px" }}>
            <div style={{ marginBottom: "8px", fontWeight: "bold" }}>{year}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
              {yearMonths.map((month) => {
                const monthStr = String(month + 1).padStart(2, "0");
                const monthValue = `${year}-${monthStr}`;
                return (
                  <Button
                    key={monthValue}
                    className={`${periodSelectorButtonClassName} ${
                      value?.type === "month" && value.value === monthValue
                        ? periodSelectorButtonSelectedClassName
                        : ""
                    }`}
                    onClick={() => handleSelect("month", monthValue)}
                    type={
                      value?.type === "month" && value.value === monthValue ? "primary" : "default"
                    }
                  >
                    {dayjs(`${year}-${monthStr}-01`).format("MMM")}
                  </Button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const content = (
    <div className={periodSelectorContentClassName}>
      <Tabs
        activeKey={selectedTab}
        onChange={setSelectedTab}
        className={periodSelectorTabsClassName}
        tabBarExtraContent={
          value && (
            <Button
              type="text"
              icon={<CloseOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(null);
              }}
            />
          )
        }
      >
        <TabPane tab="Year" key="year">
          {renderYearGrid()}
        </TabPane>
        <TabPane tab="Quarter" key="quarter">
          {renderQuarterGrid()}
        </TabPane>
        <TabPane tab="Month" key="month">
          {renderMonthGrid()}
        </TabPane>
      </Tabs>
    </div>
  );

  return (
    <div className={periodSelectorClassName}>
      <Popover
        content={content}
        trigger="click"
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom"
      >
        <Button>{getDisplayValue()}</Button>
      </Popover>
    </div>
  );
};
